import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Trash2, UserPlus, X } from 'lucide-react';
import {
  fetchCollectionMembers,
  inviteCollectionMembersByEmail,
  parseEmailList,
  removeCollectionMember,
  type CollectionMember,
} from '../../lib/collectionMembers';

interface CollectionMembersModalProps {
  isOpen: boolean;
  collectionId: string;
  collectionName: string;
  canManage: boolean;
  onClose: () => void;
}

const CollectionMembersModal: React.FC<CollectionMembersModalProps> = ({
  isOpen,
  collectionId,
  collectionName,
  canManage,
  onClose,
}) => {
  const [members, setMembers] = useState<CollectionMember[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadMembers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const rows = await fetchCollectionMembers(collectionId);
      setMembers(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load members');
    } finally {
      setIsLoading(false);
    }
  }, [collectionId]);

  useEffect(() => {
    if (isOpen) {
      setEmailInput('');
      setSuccess(null);
      setError(null);
      void loadMembers();
    }
  }, [isOpen, loadMembers]);

  if (!isOpen) return null;

  const handleInvite = async () => {
    const emails = parseEmailList(emailInput);
    if (emails.length === 0) return;

    setIsInviting(true);
    setError(null);
    setSuccess(null);

    try {
      const { invited, failed } = await inviteCollectionMembersByEmail(collectionId, emails);
      if (invited.length > 0) {
        setSuccess(
          invited.length === 1
            ? `Invited ${invited[0]}`
            : `Invited ${invited.length} members`
        );
        setEmailInput('');
        await loadMembers();
      }
      if (failed.length > 0) {
        const detail = failed.map(f => `${f.email}: ${f.message}`).join('; ');
        setError(failed.length === emails.length ? detail : `Some invites failed — ${detail}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite members');
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemove = async (userId: string) => {
    setRemovingUserId(userId);
    setError(null);
    setSuccess(null);
    try {
      await removeCollectionMember(collectionId, userId);
      setSuccess('Member removed');
      await loadMembers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove member');
    } finally {
      setRemovingUserId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[140] bg-black/60 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-slate-900 text-white rounded-lg shadow-2xl w-[520px] max-w-[92vw] border border-slate-700 flex flex-col max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-slate-700 font-semibold text-sm flex items-center justify-between">
          <span>Members — {collectionName}</span>
          <button className="p-1 hover:bg-slate-800 rounded" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="p-4 flex flex-col gap-4 overflow-y-auto">
          {canManage && (
            <div>
              <label className="block text-xs text-slate-400 mb-2">
                Invite by email (comma-separated)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="colleague@example.com, other@example.com"
                  className="flex-1 px-3 py-2 rounded bg-slate-800 border border-slate-700 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  disabled={isInviting}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleInvite();
                    }
                  }}
                />
                <button
                  className="flex items-center gap-1.5 px-3 py-2 text-xs rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
                  disabled={isInviting || !emailInput.trim()}
                  onClick={() => void handleInvite()}
                >
                  {isInviting ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
                  Invite
                </button>
              </div>
              <p className="mt-1.5 text-[11px] text-slate-500">
                Users must already have an account. Create them in Supabase Auth first if needed.
              </p>
            </div>
          )}

          {error && (
            <p className="text-xs px-2 py-1.5 rounded text-red-200 bg-red-900/30 border border-red-800/50">
              {error}
            </p>
          )}
          {success && (
            <p className="text-xs px-2 py-1.5 rounded text-green-200 bg-green-900/30 border border-green-800/50">
              {success}
            </p>
          )}

          <div>
            <h3 className="text-xs uppercase tracking-wide text-slate-400 mb-2">
              Current members ({members.length})
            </h3>
            {isLoading ? (
              <div className="flex items-center justify-center py-8 text-slate-400">
                <Loader2 size={18} className="animate-spin" />
              </div>
            ) : members.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">No members found</p>
            ) : (
              <ul className="divide-y divide-slate-700 rounded border border-slate-700 overflow-hidden">
                {members.map((member) => (
                  <li
                    key={member.userId}
                    className="flex items-center justify-between gap-3 px-3 py-2.5 bg-slate-800/50"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-white truncate">
                          {member.displayName}
                        </span>
                        {member.isOwner && (
                          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-900/50 text-blue-300 border border-blue-800/50">
                            Owner
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 truncate">{member.email}</p>
                    </div>
                    {canManage && !member.isOwner && (
                      <button
                        className="p-1.5 hover:bg-red-900/40 rounded transition-colors disabled:opacity-50"
                        title="Remove member"
                        disabled={removingUserId === member.userId}
                        onClick={() => void handleRemove(member.userId)}
                      >
                        {removingUserId === member.userId ? (
                          <Loader2 size={14} className="animate-spin text-slate-400" />
                        ) : (
                          <Trash2 size={14} className="text-red-400" />
                        )}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="px-4 py-3 border-t border-slate-700 bg-slate-900 flex justify-end">
          <button
            className="px-3 py-1.5 text-xs rounded bg-slate-700 hover:bg-slate-600"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CollectionMembersModal;
